// ADR-109 — apps/remote_assist frontend API layer
//
// NOTE (طبق الگوی همیشگی پروژه، مثلاً shopfloor قبل از AM-016): چون این
// بک‌اند تازه است، هنوز در schema.yml/orval سینک نشده — این فایل یک لایه‌ی
// دستی موقت React Query است. بعد از sync بعدی orval (طبق چرخه‌ی RM-12
// پروژه)، پیشنهاد می‌شود این فایل به هوک‌های orval واقعی (`useRemoteAssist...`
// از v1.ts) مهاجرت کند — دقیقاً همان مسیری که AM-016 برای shortageHold رفت.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// ASSUMPTION: مسیر واقعی httpClient مشترک پروژه — رجوع types.ts
import { axiosInstance as httpClient } from "@/api/client";
import type { RemoteAssistSession, RequestSessionPayload } from "./types";

const BASE = "/api/remote-assist/remote-assist-sessions";
const SETTINGS_BASE = "/api/remote-assist/remote-assist-org-settings";

const QUERY_KEY = ["remote-assist-sessions"] as const;

export function useRemoteAssistSessions(options?: { enabled?: boolean; refetchInterval?: number | false }) {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<RemoteAssistSession[]> => {
      const { data } = await httpClient.get(`${BASE}/`);
      return data.results ?? data;
    },
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval !== undefined ? options.refetchInterval : false, 
    refetchIntervalInBackground: false,
  });
}

export function useRemoteAssistSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: [...QUERY_KEY, sessionId],
    queryFn: async (): Promise<RemoteAssistSession> => {
      const { data } = await httpClient.get(`${BASE}/${sessionId}/`);
      return data;
    },
    enabled: Boolean(sessionId),
    // پولینگ برای لیست درخواست‌ها (برای جلوگیری از اسپم سرور به ۳۰ ثانیه افزایش یافت)
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });
}

export function useRequestRemoteAssistSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RequestSessionPayload): Promise<RemoteAssistSession> => {
      const { data } = await httpClient.post(`${BASE}/`, payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useAcceptRemoteAssistSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string): Promise<RemoteAssistSession> => {
      const { data } = await httpClient.post(`${BASE}/${sessionId}/accept/`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useRejectRemoteAssistSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string): Promise<RemoteAssistSession> => {
      const { data } = await httpClient.post(`${BASE}/${sessionId}/reject/`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useConfirmRemoteAssistCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      code,
    }: {
      sessionId: string;
      code: string;
    }): Promise<RemoteAssistSession> => {
      const { data } = await httpClient.post(`${BASE}/${sessionId}/confirm/`, { code });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useRevealVerificationCode(sessionId: string | undefined) {
  return useQuery({
    queryKey: [...QUERY_KEY, sessionId, "reveal-code"],
    queryFn: async (): Promise<{ verification_code: string }> => {
      const { data } = await httpClient.get(`${BASE}/${sessionId}/reveal-code/`);
      return data;
    },
    enabled: Boolean(sessionId),
    retry: false, // اگر هنوز accepted نشده، 400 برمی‌گردد — نیازی به retry نیست
  });
}

export function useEndRemoteAssistSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string): Promise<RemoteAssistSession> => {
      const { data } = await httpClient.post(`${BASE}/${sessionId}/end/`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useOrganizationRemoteAssistSettings() {
  return useQuery({
    queryKey: ["remote-assist-org-settings"],
    queryFn: async () => {
      const { data } = await httpClient.get(`${SETTINGS_BASE}/`);
      return data as { vendor_financial_masking_enabled: boolean };
    },
  });
}

export function useSetVendorFinancialMasking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data } = await httpClient.post(`${SETTINGS_BASE}/set/`, {
        vendor_financial_masking_enabled: enabled,
      });
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["remote-assist-org-settings"] }),
  });
}

import { tokenStorage } from "@/auth/tokenStorage";

export function remoteAssistWebSocketUrl(sessionId: string, vendorToken?: string): string {
  // ASSUMPTION: پروتکل/هاست WSS از یک متغیر محیطی مشترک ساخته می‌شود
  // (مثلاً VITE_WS_BASE_URL) — قبل از merge با کانفیگ واقعی پروژه هماهنگ شود.
  const base = (import.meta as any).env?.VITE_WS_BASE_URL ?? "wss://localhost";
  const url = new URL(`${base}/ws/remote-assist/${sessionId}/`);
  if (vendorToken) {
    url.searchParams.set("vendor_token", vendorToken);
  } else {
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
      url.searchParams.set("token", accessToken);
    }
  }
  return url.toString();
}
